import { useCallback, useEffect, useEffectEvent, useRef, useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { DataChangedBanner } from '@/components/DataChangedBanner';
import type { ApiResponse } from '@/lib/api-contract';
import { newerVersion, staleChangeOf, type StaleChange } from '@/lib/stale-write';
import { cn } from '@/lib/utils';
import type { TaskBoard, TaskDetail, TaskUpdateInput, TaskUpdateResponse } from '@shared/types/tasks';
import { MentionText } from './MentionText';
import { MentionTextarea } from './MentionTextarea';
import { displayToMarkup, markupToDisplay, mentionAdditions, type PickedMention } from './mention-text';

/** `base` is the description's version the edit started from; null before the task has loaded. */
type Draft = { text: string; mentions: PickedMention[]; base: string | null };

/**
 * A task's description: press it to edit (like the title), saves when you click away,
 * Esc discards. @ mentions assign people and link things when saved.
 *
 * Each save sends the version the edit started from (T-214). When someone else saved the
 * description since, nothing is saved: the editor stays open with what was typed and
 * DataChangedBanner offers Reload, which throws the draft away for their version.
 */
export function TaskDescription({
  task,
  hydrated,
  members,
  saveDescription,
  onReload,
  onNavigate,
}: {
  task: TaskDetail;
  hydrated: boolean;
  members: TaskBoard['members'];
  saveDescription: (
    input: TaskUpdateInput,
    optimistic?: Partial<TaskDetail>
  ) => Promise<ApiResponse<TaskUpdateResponse>>;
  /** Resolves true once their version has arrived. */
  onReload: () => Promise<boolean>;
  onNavigate?: () => void;
}) {
  const [draft, setDraft] = useState<Draft | null>(null);
  // This tab's own last save: its version may land before the task's refetch does.
  const [ownVersion, setOwnVersion] = useState<string | null>(null);
  // This tab's last description save still on its way: the version it started from and the one it
  // made (null if refused). A save sent meanwhile waits for it (tasks-api queues it anyway).
  const lastSave = useRef<Promise<{ from: string | null; to: string | null }> | null>(null);
  const version = newerVersion(task.description_updated_at, ownVersion);
  const [stale, setStale] = useState<StaleChange | null>(null);
  const [reloading, setReloading] = useState(false);
  // A long description folds to a few lines ("Show more"), so what's below stays in view.
  const [expanded, setExpanded] = useState(false);
  const [long, setLong] = useState(false);
  const foldRef = useCallback((el: HTMLDivElement | null) => {
    if (!el) return undefined;
    const observer = new ResizeObserver(() => {
      // Only measured while folded: unfolded, it always fits.
      if (el.dataset.folded === 'true') setLong(el.scrollHeight > el.clientHeight + 1);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  /** Only a change is sent. `closing`: the task is closing, so nothing here can show a banner. */
  const save = async (closing = false) => {
    if (!draft) return;
    // Someone else's newer save is showing: this draft would be refused again. It stays open
    // until Reload (or Esc); closing the task says it wasn't saved.
    if (stale) {
      if (closing) toast.error("Your description edit wasn't saved", { description: `${stale.changedBy ?? 'Someone'} changed it first.` });
      return;
    }
    const editing = draft;
    const description = displayToMarkup(editing.text, editing.mentions);
    setDraft(null);
    if (description === task.description) return;
    // People @'d for the first time are assigned and things are linked (by the server); show it now.
    const added = mentionAdditions(task.description, description, task, members.map((m) => m.user_id));
    const prior = lastSave.current;
    const sent = (async () => {
      // An edit opened while this tab's previous save was on its way started from that save's text:
      // it goes on the version that save made, or it would be refused as stale against itself.
      const before = prior ? await prior : null;
      const base = before?.to && before.from === editing.base ? before.to : editing.base;
      const res = await saveDescription(
        { description, ...(base ? { expected_updated_at: base } : {}) },
        { description, ...added }
      );
      return { res, base };
    })();
    const outcome = sent.then(
      ({ res, base }) => ({ from: base, to: res.error ? null : res.data?.description_updated_at ?? null }),
      () => ({ from: editing.base, to: null })
    );
    lastSave.current = outcome;
    const { res, base } = await sent;
    if (lastSave.current === outcome) lastSave.current = null;
    if (!res.error) {
      const saved = res.data?.description_updated_at;
      if (saved) {
        setOwnVersion(saved);
        // Same for an edit still open now.
        setDraft((d) => (d && d.base === base ? { ...d, base: saved } : d));
      }
      return;
    }
    // The change is undone; give the text back rather than lose it.
    setDraft(editing);
    const change = staleChangeOf(res);
    if (change) {
      setStale(change);
      if (closing) toast.error("Your description edit wasn't saved", { description: res.error });
    }
  };

  // The task closing mid-edit (before focus leaves the box) still saves what was typed.
  const saveOnClose = useEffectEvent(() => {
    if (draft) void save(true);
  });
  useEffect(() => () => saveOnClose(), []);

  const startEditing = () => {
    // Until Reload has brought their version, an edit would start from the old one.
    if (reloading) return;
    setDraft({ ...markupToDisplay(task.description), base: hydrated ? version : null });
  };

  /** Their version replaces the draft (the banner says so). A failed reload keeps both. */
  const reload = async () => {
    setReloading(true);
    const loaded = await onReload();
    setReloading(false);
    if (!loaded) {
      toast.error("Couldn't reload the description", { description: 'Your draft is still here. Try Reload again.' });
      return;
    }
    setDraft(null);
    setStale(null);
  };

  const discard = () => {
    setDraft(null);
    setStale(null);
  };

  return (
    <section className="space-y-2">
      <h3 className="text-sm font-semibold">Description</h3>
      {stale ? (
        <DataChangedBanner
          changedBy={stale.changedBy}
          what="the description"
          hasLocalEdits={draft !== null}
          onReload={() => void reload()}
          reloading={reloading}
          onDismiss={() => setStale(null)}
        />
      ) : null}
      {draft ? (
        <div className="space-y-2">
          <MentionTextarea
            aria-label="Description"
            tagging
            value={draft.text}
            onChange={(text) => setDraft((d) => (d ? { ...d, text } : d))}
            mentions={draft.mentions}
            onMentionsChange={(mentions) => setDraft((d) => (d ? { ...d, mentions } : d))}
            members={members}
            rows={5}
            autoFocus
            placeholder="Add details… @ to mention someone or something, # to tag"
            onSubmit={() => void save()}
            onBlur={() => {
              // Switching to another app or tab isn't leaving the box; it stays open for when you're back.
              if (document.hasFocus()) void save();
            }}
            onEscape={discard}
          />
          <p className="text-xs text-muted-foreground">Saves when you click away · Esc to discard</p>
        </div>
      ) : !hydrated && !task.description ? (
        <div className="space-y-2" aria-label="Loading description">
          <div className="h-3.5 w-4/5 animate-pulse rounded bg-muted" />
          <div className="h-3.5 w-3/5 animate-pulse rounded bg-muted" />
        </div>
      ) : (
        // Press to edit. Links inside still open, and selecting text to copy doesn't edit.
        <div
          role="button"
          tabIndex={0}
          aria-label="Edit description"
          className="cursor-text rounded-md border border-input/60 px-3 py-2 text-sm leading-relaxed transition-colors hover:border-input focus-visible:border-ring focus-visible:outline-none"
          onClick={(e) => {
            if ((e.target as HTMLElement).closest('a') || window.getSelection()?.toString()) return;
            startEditing();
          }}
          onKeyDown={(e) => {
            if (e.target !== e.currentTarget || (e.key !== 'Enter' && e.key !== ' ')) return;
            e.preventDefault();
            startEditing();
          }}
        >
          {task.description ? (
            <div
              ref={foldRef}
              data-folded={!expanded}
              className={cn(
                !expanded && 'max-h-48 overflow-hidden',
                !expanded && long && '[mask-image:linear-gradient(to_bottom,black_70%,transparent)]'
              )}
            >
              <MentionText text={task.description} members={members} contexts={task.contexts} onNavigate={onNavigate} />
            </div>
          ) : (
            <span className="text-muted-foreground">Add more detail…</span>
          )}
        </div>
      )}
      {!draft && task.description && (long || expanded) ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="w-full text-muted-foreground"
          onClick={() => setExpanded((e) => !e)}
        >
          {expanded ? <ChevronUp className="size-4" /> : <ChevronDown className="size-4" />}
          {expanded ? 'Show less' : 'Show more'}
        </Button>
      ) : null}
    </section>
  );
}
